import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import Tag from "ui-components/molecules/tag/tag";
import { TagColor } from "app/data/common/tagColor";
import { Popover } from "ui-components/popover/popover";
import {
  ROUTE_GUIDE_FIELD_HELP,
  ROUTE_GUIDE_FIELD_LABELS,
  ZipRoutingProfile,
  formatExtensionCode,
  formatServiceDays,
  formatServiceDayRawDisplay,
  formatStateDisplay,
  formatTerminalRichDisplay,
  formatThreePlLaneCode,
  getWeekdayServiceDayDisplayItems,
  resolveTerminalDisplayInput,
  EMPTY_SELECT_VALUE,
} from "app/data/zip-routing-profile/models";

const columnHelper = createColumnHelper<ZipRoutingProfile>();

export type RouteGuideTerminalLookup = Map<
  string,
  { airportCode?: string; terminalName?: string }
>;

export const getRouteGuideColumns = (terminalsByNumber: RouteGuideTerminalLookup) => [
  columnHelper.accessor((row) => row.zipCode || row.zip || "", {
    id: "zipCode",
    header: () => (
      <>
        ZIP
        <span onClick={(e) => e.stopPropagation()}>
          <Popover
            values={
              <div className="popover-value">
                ZIP code covered by this routing profile.
              </div>
            }
          />
        </span>
      </>
    ),
    size: 50,
  }),
  columnHelper.accessor("state", {
    id: "state",
    header: "State",
    cell: (cellProps) => formatStateDisplay(cellProps.getValue()),
    size: 110,
  }),
  columnHelper.accessor("terminalNumber", {
    id: "terminal",
    header: () => (
      <>
        Terminal
        <span onClick={(e) => e.stopPropagation()}>
          <Popover
            values={
              <div className="popover-value">
                Servicing terminal assigned from the route guide for this ZIP.
              </div>
            }
          />
        </span>
      </>
    ),
    cell: (cellProps) => {
      const row = cellProps.row.original;
      if (row.coverageStatus === "NOT_SERVICED" || row.terminalNumber == null) {
        return "—";
      }
      return formatTerminalRichDisplay(
        resolveTerminalDisplayInput(row, terminalsByNumber)
      );
    },
    size: 160,
  }),
  columnHelper.accessor((row) => row.coverageStatus || "SERVICED", {
    id: "coverageStatus",
    header: () => (
      <>
        Coverage
        <span onClick={(e) => e.stopPropagation()}>
          <Popover
            values={
              <div className="popover-value">
                Whether this ZIP is serviced or not serviced in the route guide.
              </div>
            }
          />
        </span>
      </>
    ),
    cell: (cellProps) => {
      const value = cellProps.getValue();
      const serviced = value !== "NOT_SERVICED";
      return (
        <Tag
          mods={{
            color: serviced ? TagColor.GREEN : TagColor.GREY,
            uppercase: true,
          }}
        >
          {serviced ? "Serviced" : "Not Serviced"}
        </Tag>
      );
    },
    size: 70,
  }),
  columnHelper.accessor(
    (row) => {
      if (typeof row.direct === "boolean") return row.direct;
      if (row.rateType === "I") return false;
      if (row.rateType === "D") return true;
      return true;
    },
    {
      id: "direct",
      header: () => (
        <>
          Delivery Point
          <span onClick={(e) => e.stopPropagation()}>
            <Popover
              values={
                <div className="popover-value">
                  Direct vs indirect delivery point for this ZIP.
                </div>
              }
            />
          </span>
        </>
      ),
      cell: (cellProps) => (
        <Tag
          mods={{
            color: cellProps.getValue() ? TagColor.GREEN : TagColor.ORANGE,
            uppercase: true,
          }}
        >
          {cellProps.getValue() ? "Direct" : "Indirect"}
        </Tag>
      ),
      size: 55,
    }
  ),
  // Quote column hidden for now — keep quoteAllowed in API payload defaults
  // columnHelper.accessor((row) => !!row.quoteAllowed, {
  //   id: "quoteAllowed",
  //   ...
  // }),
  columnHelper.accessor((row) => formatThreePlLaneCode(row.threePlOriginCode), {
    id: "obm",
    header: () => (
      <>
        {ROUTE_GUIDE_FIELD_LABELS.threePlOriginCode}
        <span onClick={(e) => e.stopPropagation()}>
          <Popover
            values={
              <div className="popover-value">
                {ROUTE_GUIDE_FIELD_HELP.threePlOriginCode}
              </div>
            }
          />
        </span>
      </>
    ),
    size: 70,
  }),
  columnHelper.accessor((row) => formatThreePlLaneCode(row.threePlDestCode), {
    id: "ibm",
    header: () => (
      <>
        {ROUTE_GUIDE_FIELD_LABELS.threePlDestCode}
        <span onClick={(e) => e.stopPropagation()}>
          <Popover
            values={
              <div className="popover-value">
                {ROUTE_GUIDE_FIELD_HELP.threePlDestCode}
              </div>
            }
          />
        </span>
      </>
    ),
    size: 80,
  }),
  columnHelper.accessor((row) => formatExtensionCode(row.extensionCode1), {
    id: "extra1",
    header: () => (
      <>
        {ROUTE_GUIDE_FIELD_LABELS.extensionCode1}
        <span onClick={(e) => e.stopPropagation()}>
          <Popover
            values={
              <div className="popover-value">
                {ROUTE_GUIDE_FIELD_HELP.extensionCode1}
              </div>
            }
          />
        </span>
      </>
    ),
    size: 70,
  }),
  columnHelper.accessor((row) => formatExtensionCode(row.extensionCode2), {
    id: "extra2",
    header: () => (
      <>
        {ROUTE_GUIDE_FIELD_LABELS.extensionCode2}
        <span onClick={(e) => e.stopPropagation()}>
          <Popover
            values={
              <div className="popover-value">
                {ROUTE_GUIDE_FIELD_HELP.extensionCode2}
              </div>
            }
          />
        </span>
      </>
    ),
    size: 70,
  }),
  columnHelper.display({
    id: "serviceDays",
    header: () => (
      <>
        Service Days
        <span onClick={(e) => e.stopPropagation()}>
          <Popover
            values={
              <div className="popover-value">
                Mon–Fri shown for every row. Values are AS400 codes (Y, I, 0–9) or —
                when blank.
              </div>
            }
          />
        </span>
      </>
    ),
    cell: (cellProps) => {
      const items = getWeekdayServiceDayDisplayItems(cellProps.row.original);
      return (
        <div
          className="xgs-route-guide__days-cell"
          title={formatServiceDays(cellProps.row.original)}
        >
          {items.map(({ day, label, code }) => {
            const isEmpty = !code || code === EMPTY_SELECT_VALUE;
            return (
              <div
                key={day}
                className={
                  isEmpty
                    ? "xgs-route-guide__days-cell__item xgs-route-guide__days-cell__item--empty"
                    : "xgs-route-guide__days-cell__item"
                }
              >
                <span className="xgs-route-guide__days-cell__label">{label}</span>
                {/* REVERT (friendly labels):
                {isEmpty ? (
                  <span className="xgs-route-guide__days-cell__code">No Delivery</span>
                ) : (
                  <span className="xgs-route-guide__days-cell__code">{display}</span>
                )}
                REVERT: formatServiceDayCodeDisplay / formatServiceDayCodeDisplayWithCode(code)
                */}
                <span className="xgs-route-guide__days-cell__code">
                  {formatServiceDayRawDisplay(code)}
                </span>
              </div>
            );
          })}
        </div>
      );
    },
    size: 360,
    minSize: 320,
  }),
  columnHelper.accessor("dateUpdated", {
    id: "dateUpdated",
    header: "Last Updated",
    cell: (cellProps) => {
      const value = cellProps.getValue();
      if (!value?.trim()) return "—";
      return value.toUiDateTimeFormat?.() || value;
    },
    size: 90,
  }),
];
